import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateGratuity, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const GratuityCalculator = () => {
  const [basicSalary, setBasicSalary] = useState(50000);
  const [da, setDa] = useState(10000);
  const [yearsOfService, setYearsOfService] = useState(15);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [basicSalary, da, yearsOfService]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const bs = Number(basicSalary) || 0;
    const d = Number(da) || 0;
    const yos = Number(yearsOfService) || 0;
    const lastDrawnSalary = bs + d;
    const gratuityResult = calculateGratuity(lastDrawnSalary, yos);
    setResult(gratuityResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('Gratuity Calculator Report', {
      'Basic Salary': basicSalary,
      'Dearness Allowance': da,
      'Last Drawn Salary': basicSalary + da,
      'Years of Service': yearsOfService,
      'Calculated Gratuity': result.calculatedAmount,
      'Maximum Limit': 2000000,
      'Payable Gratuity': result.gratuityAmount,
      'Maximum Limit Applied': result.isMaxLimitApplied ? 'Yes' : 'No',
    });
  };

  const handleReset = () => {
    setBasicSalary(50000);
    setDa(10000);
    setYearsOfService(15);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Gratuity Calculator"
        description="Free Gratuity Calculator India - Calculate gratuity amount based on years of service and last drawn salary."
        keywords="gratuity calculator, gratuity calculator India, gratuity calculation formula, gratuity amount calculator, employee gratuity calculator"
        canonicalPath="/gratuity"
      />
      <div className="calculator-header">
        <h1>Gratuity Calculator</h1>
        <p>Calculate your gratuity amount based on Payment of Gratuity Act, 1972</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Basic Salary (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={basicSalary === '' ? '' : basicSalary}
                onChange={(e) => setBasicSalary(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="1000000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Dearness Allowance (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={da === '' ? '' : da}
                onChange={(e) => setDa(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="500000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Years of Service</label>
            <input
              type="number"
              value={yearsOfService === '' ? '' : yearsOfService}
              onChange={(e) => setYearsOfService(e.target.value === '' ? '' : Number(e.target.value))}
              min="5"
              max="50"
            />
            <div className="slider-container">
              <input
                type="range"
                min="5"
                max="50"
                step="1"
                value={yearsOfService || 0}
                onChange={(e) => setYearsOfService(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${(((yearsOfService || 0) - 5) / 45) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Minimum 5 years of continuous service required
            </small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Gratuity Amount</h3>
                <div className="amount">{formatCurrency(result.gratuityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Last Drawn Salary (Basic + DA)</span>
                  <span className="value">{formatCurrency(basicSalary + da)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Years of Service</span>
                  <span className="value">{yearsOfService} years</span>
                </div>
                <div className="result-item">
                  <span className="label">Calculated Amount</span>
                  <span className="value">{formatCurrency(result.calculatedAmount)}</span>
                </div>
                {result.isMaxLimitApplied && (
                  <div className="result-item" style={{ background: '#fef3c7' }}>
                    <span className="label">⚠️ Maximum Limit Applied</span>
                    <span className="value">{formatCurrency(2000000)}</span>
                  </div>
                )}
              </div>

              <div className="info-box" style={{ marginTop: '20px', background: '#f0fdf4', borderColor: '#86efac' }}>
                <h4>Gratuity Formula</h4>
                <p style={{ fontFamily: 'monospace', marginTop: '8px' }}>
                  Gratuity = (Last Drawn Salary × 15 × Years of Service) / 26
                </p>
                <p style={{ marginTop: '8px' }}>
                  = ({formatCurrency(basicSalary + da)} × 15 × {yearsOfService}) / 26
                </p>
                <p style={{ marginTop: '4px' }}>
                  = {formatCurrency(result.calculatedAmount)}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Gratuity</h4>
        <ul>
          <li><strong>Eligibility:</strong> Minimum 5 years of continuous service</li>
          <li><strong>Maximum Limit:</strong> ₹20,00,000 (increased from ₹10 lakh in 2018)</li>
          <li><strong>Tax Exemption:</strong> Up to ₹20 lakh is tax-free for government employees</li>
          <li><strong>For Private Sector:</strong> Least of: Actual gratuity, ₹20 lakh, or formula-based amount is exempt</li>
          <li><strong>15/26:</strong> 15 days' wages for every completed year, 26 working days in a month</li>
        </ul>
      </div>
    </div>
  );
};

export default GratuityCalculator;
